import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  Palette, 
  Film, 
  Users, 
  MessageSquare, 
  Calendar,
  Clock,
  Smartphone,
  ChevronLeft,
  LogOut,
  ShoppingCart,
  Music
} from 'lucide-react';

const sidebarLinks = [
  { href: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/admin/content', label: 'Content', icon: FileText },
  { href: '/admin/themes', label: 'Themes', icon: Palette },
  { href: '/admin/media', label: 'Media', icon: Film },
  { href: '/admin/audio', label: 'Audio', icon: Music },
  { href: '/admin/apps', label: 'Apps', icon: Smartphone },
  { href: '/admin/users', label: 'Users', icon: Users },
  { href: '/admin/messages', label: 'Messages', icon: MessageSquare },
  { href: '/admin/sessions', label: 'Sessions', icon: Calendar },
  { href: '/admin/slots', label: 'Time Slots', icon: Clock },
  { href: '/admin/purchase-settings', label: 'Purchase Settings', icon: ShoppingCart },
];

export const AdminLayout = ({ children, title }) => {
  const { currentAdminTheme } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();

  // Use admin theme for the admin panel
  const theme = currentAdminTheme;

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: theme.background }}>
      {/* Sidebar */}
      <aside 
        className="admin-sidebar flex flex-col w-[280px] min-h-screen"
        style={{ backgroundColor: theme.isDark ? theme.muted : theme.foreground }}
      >
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})` }}
            >
              <span className="text-white font-display font-bold text-xl">N</span>
            </div>
            <span className="font-display text-xl font-medium text-white">
              Admin
            </span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 overflow-y-auto">
          {sidebarLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="flex items-center gap-3 px-6 py-3 mx-3 rounded-lg transition-colors"
              style={{
                color: isActive(link.href) ? 'white' : 'rgba(255,255,255,0.7)',
                backgroundColor: isActive(link.href) ? theme.primary : 'transparent'
              }}
              data-testid={`admin-nav-${link.label.toLowerCase().replace(' ', '-')}`}
            >
              <link.icon className="w-5 h-5" />
              {link.label}
            </Link>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
            ) : (
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: theme.primary }}
              >
                <span className="text-white font-medium">{user?.name?.charAt(0)}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name}</p>
              <p className="text-white/50 text-sm truncate">@{user?.username}</p>
            </div>
          </div>
          <Link 
            to="/" 
            className="flex items-center gap-3 px-6 py-3 mx-3 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Site
          </Link>
          <button 
            onClick={logout}
            className="flex items-center gap-3 px-6 py-3 mx-3 rounded-lg text-red-400 hover:text-red-300 hover:bg-white/10 transition-colors w-full"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header 
          className="h-20 px-8 flex items-center border-b"
          style={{ 
            borderColor: theme.border, 
            backgroundColor: theme.cardBg || (theme.isDark ? theme.muted : `${theme.background}f0`)
          }}
        >
          <h1 
            className="font-display text-2xl font-medium" 
            style={{ color: theme.cardText || theme.foreground }}
          >
            {title}
          </h1>
        </header>

        {/* Content */}
        <div className="p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
